import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { Observable, tap } from 'rxjs';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface VerifyOtpRequest {
  email: string;
  otp: string;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber?: string;
  password: string;
  transactionPin: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber?: string;
    dateCreated: string;
    isActive: boolean;
    token?: string;
    expiresAt?: string;
  } | null;
}

export interface ProfileResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber?: string;
    dateCreated: string;
    isActive: boolean;
    isEmailVerified: boolean;
  } | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'arkifihub_token';
  private readonly USER_KEY = 'arkifihub_user';

  private token = signal<string | null>(this.getStoredToken());
  private user = signal<any | null>(this.getStoredUser());

  isAuthenticated = computed(() => !!this.token());

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Check if token is expired on initialization
    this.checkTokenExpiry();
  }

  sendLoginOtp(credentials: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(
      `${environment.apiUrl}/api/Auth/send-login-otp`,
      credentials
    );
  }

  verifyLoginOtp(request: VerifyOtpRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(
      `${environment.apiUrl}/api/Auth/verify-login-otp`,
      request
    ).pipe(
      tap(response => {
        if (response.success && response.data?.token) {
          this.setAuth(response.data.token, response.data);
        }
      })
    );
  }

  register(request: RegisterRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(
      `${environment.apiUrl}/api/Auth/register`,
      request
    );
  }

  sendEmailVerificationOtp(email: string): Observable<{ success: boolean; message: string; data: { message: string; expiresAt: string } | null }> {
    return this.http.post<{ success: boolean; message: string; data: { message: string; expiresAt: string } | null }>(
      `${environment.apiUrl}/api/Auth/send-email-verification-otp`,
      { email }
    );
  }

  verifyEmailOtp(email: string, otp: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${environment.apiUrl}/api/Auth/verify-email-otp`,
      { email, otp }
    );
  }

  sendForgotPasswordOtp(email: string): Observable<{ success: boolean; message: string; data: { message: string; expiresAt: string } | null }> {
    return this.http.post<{ success: boolean; message: string; data: { message: string; expiresAt: string } | null }>(
      `${environment.apiUrl}/api/Auth/forgot-password`,
      { email }
    );
  }

  resendForgotPasswordOtp(email: string): Observable<{ success: boolean; message: string; data: { message: string; expiresAt: string } | null }> {
    return this.http.post<{ success: boolean; message: string; data: { message: string; expiresAt: string } | null }>(
      `${environment.apiUrl}/api/Auth/resend-forgot-password-otp`,
      { email }
    );
  }

  verifyForgotPasswordOtp(email: string, otp: string): Observable<{ success: boolean; message: string; data: { resetToken: string; expiresAt: string } | null }> {
    return this.http.post<{ success: boolean; message: string; data: { resetToken: string; expiresAt: string } | null }>(
      `${environment.apiUrl}/api/Auth/verify-forgot-password-otp`,
      { email, otp }
    );
  }

  resetPassword(resetToken: string, newPassword: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${environment.apiUrl}/api/Auth/reset-password?resetToken=${encodeURIComponent(resetToken)}`,
      { newPassword }
    );
  }

  getProfile(): Observable<ProfileResponse> {
    const token = this.getToken();
    const headers: { [key: string]: string } = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http.get<ProfileResponse>(
      `${environment.apiUrl}/api/ArkifiHubProfile/profile`,
      { headers }
    );
  }

  logout(): void {
    this.token.set(null);
    this.user.set(null);
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.router.navigate(['/']);
  }

  getToken(): string | null {
    // Always check localStorage first to ensure we have the latest token
    const storedToken = localStorage.getItem(this.TOKEN_KEY);
    if (storedToken) {
      // Update signal if it's different
      if (this.token() !== storedToken) {
        this.token.set(storedToken);
      }
      return storedToken;
    }
    return this.token();
  }

  getUser(): any | null {
    return this.user();
  }

  private setAuth(token: string, user: any): void {
    console.log('Setting authentication token and user data');
    this.token.set(token);
    this.user.set(user);
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    console.log('Token stored successfully. Token length:', token.length);
  }

  private getStoredToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  private getStoredUser(): any | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  private checkTokenExpiry(): void {
    const user = this.getStoredUser();
    if (user?.expiresAt) {
      const expiresAt = new Date(user.expiresAt);
      if (expiresAt < new Date()) {
        this.logout();
      }
    }
  }
}
